import React, { useState, useEffect } from "react";
import { confirm } from "../utils/confirm";
import { X, Plus, Trash2, Type, Hash, Calendar, List, CheckSquare, Euro, GripVertical, Link, Unlink } from "lucide-react";
import { collection, addDoc, onSnapshot, deleteDoc, doc, updateDoc, serverTimestamp, query, where, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useToast } from "../utils/Toast";

const FIELD_TYPES = [
  { value:"testo",   label:"Testo",      icon:Type },
  { value:"numero",  label:"Numero",     icon:Hash },
  { value:"data",    label:"Data",       icon:Calendar },
  { value:"tendina", label:"Tendina",    icon:List },
  { value:"checkbox",label:"Checkbox",   icon:CheckSquare },
  { value:"valuta",  label:"Valuta (€)", icon:Euro },
];

// Campi del cliente collegabili
const CLIENTE_FIELDS = [
  { key:"ragioneSociale", label:"Ragione Sociale" },
  { key:"referente",      label:"Referente" },
  { key:"email",          label:"Email" },
  { key:"telefono",       label:"Telefono" },
  { key:"indirizzo",      label:"Indirizzo" },
  { key:"piva",           label:"P.IVA / Codice Fiscale" },
  { key:"settore",        label:"Settore" },
];

export default function CustomFieldsManagerPanel({ sectionId, sectionName, projectId, onClose }) {
  const { addToast } = useToast();
  const [fields, setFields] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ name:"", type:"testo", required:false, placeholder:"", options:"", linkedClienteField:"" });
  const [loading, setLoading] = useState(false);
  const [clienteId, setClienteId] = useState(null);
  const [showLinkFor, setShowLinkFor] = useState(null); // fieldId being linked

  // Carica clienteId del progetto
  useEffect(() => {
    if (!projectId) return;
    getDoc(doc(db,"projects",projectId)).then(snap => {
      if (snap.exists()) setClienteId(snap.data().clienteId || null);
    });
  }, [projectId]);

  useEffect(() => {
    const q = query(collection(db,"customFields"), where("sectionId","==",sectionId));
    return onSnapshot(q, snap => {
      const list = snap.docs.map(d=>({id:d.id,...d.data()}));
      // ordine: campi creati insieme (es. modelli importati); gli altri seguono per data di creazione
      list.sort((a,b)=>((a.ordine??1000)-(b.ordine??1000)) || ((a.createdAt?.toMillis?.()||0)-(b.createdAt?.toMillis?.()||0)));
      setFields(list);
    });
  }, [sectionId]);

  const save = async e => {
    e.preventDefault();
    if (!form.name.trim()) { addToast("Inserisci un nome","error"); return; }
    setLoading(true);
    await addDoc(collection(db,"customFields"), {
      name: form.name.trim(),
      type: form.type,
      required: form.required,
      placeholder: form.placeholder.trim(),
      options: form.type==="tendina" ? form.options.split("\n").map(o=>o.trim()).filter(Boolean) : [],
      linkedClienteField: form.linkedClienteField || null,
      sectionId,
      projectId: projectId || null,
      createdAt: serverTimestamp()
    });
    setForm({ name:"", type:"testo", required:false, placeholder:"", options:"", linkedClienteField:"" });
    setAdding(false);
    addToast("Campo aggiunto!", "success");
    setLoading(false);
  };

  const del = async field => {
    if (!await confirm(`Eliminare il campo "${field.name}"?`, { type:"danger" })) return;
    await deleteDoc(doc(db,"customFields",field.id));
    addToast("Campo eliminato","info");
  };

  const linkField = async (fieldId, clienteFieldKey) => {
    await updateDoc(doc(db,"customFields",fieldId), { linkedClienteField: clienteFieldKey || null });
    setShowLinkFor(null);
    addToast(clienteFieldKey ? "Campo collegato al cliente!" : "Collegamento rimosso", "success");
  };

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal" style={{maxWidth:540, maxHeight:"90vh", overflowY:"auto"}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:20}}>
          <div>
            <h3 style={{fontSize:16,fontWeight:700}}>Campi dati</h3>
            <p style={{fontSize:13,color:"#6b7280",marginTop:2}}>Sezione: {sectionName}</p>
          </div>
          <button className="btn-icon" onClick={onClose}><X size={16}/></button>
        </div>

        {/* Info field mapping */}
        {clienteId && (
          <div style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:12,color:"#1e40af",display:"flex",alignItems:"flex-start",gap:8}}>
            <Link size={13} style={{marginTop:1,flexShrink:0}}/>
            <span>Questa commessa ha un cliente collegato. Puoi collegare i campi dati ai dati del cliente — il valore verrà ereditato automaticamente.</span>
          </div>
        )}

        {/* Lista campi esistenti */}
        {fields.length===0 && !adding ? (
          <div style={{textAlign:"center",padding:24,color:"#9ca3af",border:"1px dashed #e5e7eb",borderRadius:8,marginBottom:16}}>
            <p style={{fontSize:14,marginBottom:8}}>Nessun campo dati</p>
            <p style={{fontSize:13}}>I campi saranno visibili solo nelle attività di questa sezione</p>
          </div>
        ) : (
          <div style={{marginBottom:16}}>
            {fields.map(field => {
              const ft = FIELD_TYPES.find(t=>t.value===field.type);
              const isLinked = !!field.linkedClienteField;
              const linkedLabel = CLIENTE_FIELDS.find(f=>f.key===field.linkedClienteField)?.label;
              return (
                <div key={field.id} style={{marginBottom:8}}>
                  <div style={{display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:"#f9fafb",borderRadius:8,border:`1px solid ${isLinked?"#bfdbfe":"#e5e7eb"}`}}>
                    <GripVertical size={14} color="#9ca3af"/>
                    <div style={{width:28,height:28,borderRadius:6,background:isLinked?"#eff6ff":"#ede9fe",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      {ft && <ft.icon size={14} color={isLinked?"#2563eb":"#7c3aed"}/>}
                    </div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:600}}>{field.name}{field.required&&<span style={{color:"#dc2626"}}> *</span>}</div>
                      <div style={{fontSize:11,color:"#9ca3af",display:"flex",alignItems:"center",gap:6}}>
                        {ft?.label}
                        {isLinked && <span style={{color:"#2563eb",display:"flex",alignItems:"center",gap:3}}><Link size={10}/>collegato a "{linkedLabel}"</span>}
                      </div>
                    </div>
                    {/* Pulsante collegamento cliente */}
                    {clienteId && (
                      <button onClick={()=>setShowLinkFor(showLinkFor===field.id?null:field.id)}
                        className="btn-icon" title={isLinked?"Gestisci collegamento":"Collega a cliente"}
                        style={{color:isLinked?"#2563eb":"#9ca3af"}}>
                        {isLinked ? <Link size={14}/> : <Unlink size={14}/>}
                      </button>
                    )}
                    <button className="btn-icon" onClick={()=>del(field)} style={{color:"#dc2626"}}><Trash2 size={14}/></button>
                  </div>

                  {/* Dropdown collegamento */}
                  {showLinkFor===field.id && (
                    <div style={{background:"#eff6ff",border:"1px solid #bfdbfe",borderRadius:8,padding:12,marginTop:4}}>
                      <div style={{fontSize:12,fontWeight:600,color:"#1e40af",marginBottom:8}}>Collega "{field.name}" a campo cliente:</div>
                      <div style={{display:"flex",flexWrap:"wrap",gap:6,marginBottom:8}}>
                        {CLIENTE_FIELDS.map(cf=>(
                          <button key={cf.key} onClick={()=>linkField(field.id,cf.key)}
                            style={{padding:"4px 10px",border:`1px solid ${field.linkedClienteField===cf.key?"#2563eb":"#bfdbfe"}`,borderRadius:20,
                              background:field.linkedClienteField===cf.key?"#2563eb":"#fff",
                              color:field.linkedClienteField===cf.key?"#fff":"#1e40af",
                              fontSize:11,fontWeight:500,cursor:"pointer",fontFamily:"'Inter',sans-serif"}}>
                            {cf.label}
                          </button>
                        ))}
                      </div>
                      {isLinked && (
                        <button onClick={()=>linkField(field.id,null)}
                          style={{fontSize:11,color:"#dc2626",background:"none",border:"none",cursor:"pointer",display:"flex",alignItems:"center",gap:4,fontFamily:"'Inter',sans-serif"}}>
                          <Unlink size={11}/> Rimuovi collegamento
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Form aggiunta campo */}
        {adding ? (
          <form onSubmit={save} style={{background:"#f9fafb",border:"1px solid #7c3aed",borderRadius:8,padding:16}}>
            <div className="form-group">
              <label className="form-label">Nome del campo</label>
              <input className="input" value={form.name} onChange={e=>setForm(f=>({...f,name:e.target.value}))}
                placeholder="Es. Numero contratto, Referente, Budget..." required autoFocus/>
            </div>

            <div className="form-group">
              <label className="form-label">Tipo</label>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:6}}>
                {FIELD_TYPES.map(t=>(
                  <button key={t.value} type="button" onClick={()=>setForm(f=>({...f,type:t.value}))}
                    style={{display:"flex",alignItems:"center",gap:6,padding:"8px 10px",
                      border:`2px solid ${form.type===t.value?"#7c3aed":"#e5e7eb"}`,
                      borderRadius:6,background:form.type===t.value?"#ede9fe":"transparent",
                      color:form.type===t.value?"#7c3aed":"#6b7280",
                      cursor:"pointer",fontSize:12,fontFamily:"'Inter',sans-serif",fontWeight:500}}>
                    <t.icon size={13}/>{t.label}
                  </button>
                ))}
              </div>
            </div>

            {form.type==="tendina" && (
              <div className="form-group">
                <label className="form-label">Opzioni (una per riga)</label>
                <textarea className="input" value={form.options} onChange={e=>setForm(f=>({...f,options:e.target.value}))}
                  placeholder={"Opzione 1\nOpzione 2\nOpzione 3"} rows={4}/>
              </div>
            )}

            {form.type!=="checkbox" && form.type!=="tendina" && (
              <div className="form-group">
                <label className="form-label">Testo suggerito (opzionale)</label>
                <input className="input" value={form.placeholder} onChange={e=>setForm(f=>({...f,placeholder:e.target.value}))}
                  placeholder="Es. Inserisci il valore..."/>
              </div>
            )}

            {/* Collegamento cliente già in fase di creazione */}
            {clienteId && (
              <div className="form-group">
                <label className="form-label">Collega a campo cliente (opzionale)</label>
                <select className="input" value={form.linkedClienteField}
                  onChange={e=>setForm(f=>({...f,linkedClienteField:e.target.value}))}>
                  <option value="">Nessun collegamento — valore indipendente</option>
                  {CLIENTE_FIELDS.map(cf=>(
                    <option key={cf.key} value={cf.key}>{cf.label}</option>
                  ))}
                </select>
                {form.linkedClienteField && (
                  <div style={{fontSize:11,color:"#2563eb",marginTop:4,display:"flex",alignItems:"center",gap:4}}>
                    <Link size={10}/> Il valore verrà ereditato dal cliente e aggiornato automaticamente
                  </div>
                )}
              </div>
            )}

            <div className="form-group">
              <label style={{display:"flex",alignItems:"center",gap:8,cursor:"pointer",fontSize:13,color:"#374151"}}>
                <input type="checkbox" checked={form.required} onChange={e=>setForm(f=>({...f,required:e.target.checked}))} style={{accentColor:"#7c3aed"}}/>
                Campo obbligatorio
              </label>
            </div>

            <div style={{display:"flex",gap:8,justifyContent:"flex-end"}}>
              <button type="button" className="btn btn-secondary" onClick={()=>setAdding(false)}>Annulla</button>
              <button type="submit" className="btn btn-primary" disabled={loading}>{loading?"Salvo...":"Aggiungi campo"}</button>
            </div>
          </form>
        ) : (
          <button className="btn btn-secondary" onClick={()=>setAdding(true)} style={{width:"100%",justifyContent:"center"}}>
            <Plus size={14}/> Nuovo campo dati
          </button>
        )}
      </div>
    </div>
  );
}
